import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GreyChevronIcon from "../../../../assets/icons/grey-bottom-chevron.svg";
import PlayIcon from "../../../../assets/icons/play.svg";
import LockIcon from "../../../../assets/icons/lock-outlined-without-point.svg";
import AudioIcon from "../../../../assets/icons/white-audio.svg";
import { chatScriptService } from "../../../../services/chat-script.service";
import { ScriptDetailDto } from "../../../../helpers/types";
import Loader from "../../../loader/Loader";
import "./DetailChatScriptModal.less";

export type DetailChatScriptModalProps = {
  id: string;
  onClose: () => void;
};

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className={`detail-section ${open ? "open" : ""}`} onClick={() => setOpen((v) => !v)}>
      <div className="detail-section-header">
        <div className="detail-section-title">{title}</div>
        <button type="button" className="detail-section-toggle" aria-label="toggle">
          <img src={GreyChevronIcon} alt="toggle" />
        </button>
      </div>
      {open && <div className="detail-section-body">{children}</div>}
    </div>
  );
};

const DetailChatScriptModal: React.FC<DetailChatScriptModalProps> = ({ id, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ScriptDetailDto | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  const title = data?.title;
  const message = data?.message;
  const note = data?.notes || undefined;
  const price = (data?.price as number | null | undefined) ?? null;
  const category = data?.categoryId?.name || undefined;
  type UIMedia = { id: string; name: string; thumbnailUrl?: string; url?: string; mimeType?: string; isPrivate?: boolean };
  const media: UIMedia[] = Array.isArray(data?.files)
    ? data!.files.map((f) => ({ id: f._id, name: f.name, thumbnailUrl: f.thumbnailUrl, url: f.url, mimeType: f.mimeType, isPrivate: f.isPrivate }))
    : [];
  const isFree = !price || price <= 0;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await chatScriptService.getScriptById(id);
        if (mounted) setData(res);
      } catch (e) {
        console.error("Failed to fetch script details", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <div
      className="detail-chat-script-modal-overlay"
      onClick={() => {
        if (previewImageUrl || previewVideoUrl) return;
        onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="detail-chat-script-modal-content" onClick={(e) => e.stopPropagation()} role="document">
        <div className="modal-header-line-wrapper" onClick={onClose}>
          <div className="modal-header-line"></div>
        </div>

        <div className="detail-chat-script-modal-wrapper">
          <div className="detail-chat-script-modal-title">
            {t("script-detail-title")} {title || ""}
          </div>

          {loading ? (
            <div className="detail-chat-script-loader">
              <Loader />
            </div>
          ) : (
            <div className="detail-chat-script-modal-sections">
              {!!note && (
                <Section title={t("note")}>
                  <p className="detail-text">{note}</p>
                </Section>
              )}

              {media.length > 0 && (
                <Section title={t("media")}>
                  <ul className="detail-media-list">
                    {media.map((m) => {
                      const isVideo = (m.mimeType || "").toLowerCase().startsWith("video");
                      const isAudio = (m.mimeType || "").toLowerCase().startsWith("audio");
                      const isPrivate = !!m.isPrivate;
                      return (
                        <li key={m.id} className="detail-media-item" onClick={(e) => e.stopPropagation()}>
                          {isAudio ? (
                            <div className="detail-media-audio-container">
                              <img src={AudioIcon} alt="audio" className="detail-media-audio-icon" />
                            </div>
                          ) : (
                            <img
                              src={m.thumbnailUrl || m.url}
                              alt={m.name}
                              className="detail-media-thumb"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isVideo) {
                                  if (m.url) setPreviewVideoUrl(m.url);
                                } else if (m.url) {
                                  setPreviewImageUrl(m.url);
                                }
                              }}
                              role="button"
                              aria-label={isVideo ? "video-thumbnail" : "image-thumbnail"}
                            />
                          )}
                          {isPrivate && <img src={LockIcon} alt="locked" className="detail-media-lock" />}
                          {isVideo && <img src={PlayIcon} alt="play" className="detail-media-play-topright" />}
                        </li>
                      );
                    })}
                  </ul>
                </Section>
              )}

              {!!message && (
                <Section title={t("message")}>
                  <p className="detail-text">{message}</p>
                </Section>
              )}

              {price !== null && price !== undefined && (
                <Section title={t("price")}>
                  <p className="detail-price">{isFree ? t("free") : `${price!.toFixed(2)} €`}</p>
                </Section>
              )}

              {!!category && (
                <Section title={t("category")}>
                  <p className="detail-text">{category}</p>
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
      {previewImageUrl && (
        <div className="detail-media-preview-overlay" onClick={() => setPreviewImageUrl(null)} aria-modal="true" role="dialog">
          <img src={previewImageUrl} alt="preview" className="detail-media-preview-image" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      {previewVideoUrl && (
        <div className="detail-media-preview-overlay" onClick={() => setPreviewVideoUrl(null)} aria-modal="true" role="dialog">
          <video
            src={previewVideoUrl}
            className="detail-media-preview-video"
            onClick={(e) => e.stopPropagation()}
            controls
            autoPlay
            playsInline
            style={{ maxWidth: "90vw", maxHeight: "80vh" }}
          />
        </div>
      )}
    </div>
  );
};

export default DetailChatScriptModal;
